import { useEffect, useMemo, useState } from 'react';
import { incidentService, Incident } from '../../api/incidents';
import { vehicleService, Vehicle } from '../../api/vehicles';
import { employeeService, Employee } from '../../api/Employee';
import FilterOptions from './components/FilterOptions';
import {
  Search,
  Car,
  User,
  Calendar,
  Clock,
  FileText,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  X,
  Image as ImageIcon,
  RefreshCw,
  Filter,
  MapPin,
  AlertTriangle,
  Users,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const HistoricoDeIncidentes = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [_error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string[]>>({});
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2800); return () => clearTimeout(t); } }, [toast]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [allIncidents, allVehicles, allEmployees] = await Promise.all([
        incidentService.getAll(),
        vehicleService.getAllVehicles(),
        employeeService.getAllEmployees(),
      ]);
      setIncidents(allIncidents);
      setVehicles(allVehicles);
      setEmployees(allEmployees);
    } catch (_err: unknown) {
      setError('Erro ao buscar histórico de incidentes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filterCategories = useMemo(() => ({
    Veículo: Array.from(new Set(incidents.map((incident) => incident.vehicle_id)))
      .map((vehicleId) => {
        const vehicle = vehicles.find((item) => item.id === vehicleId);
        return {
          id: String(vehicleId),
          label: vehicle ? `${vehicle.brand || 'Veículo'} - ${vehicle.license_plate}` : `Veículo ${vehicleId}`,
        };
      }),
    Fotos: [
      { id: 'com_fotos', label: 'Com fotos' },
      { id: 'sem_fotos', label: 'Sem fotos' },
    ],
  }), [incidents, vehicles]);

  const getVehicleInfo = (vehicle_id: number) => {
    const v = vehicles.find(v => v.id === vehicle_id);
    return v ? { primary: v.brand, secondary: `Placa: ${v.license_plate}`, display: `${v.brand} - Placa: ${v.license_plate}` } : { primary: `Veículo ${vehicle_id}`, secondary: '', display: String(vehicle_id) };
  };

  const renderInvolvedEmployees = (ids: number[] = []) => (
    <div className="flex flex-col gap-2">
      {ids.map(id => {
        const emp = employees.find(e => e.id === id);
        return emp ? (
          <div key={id} className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
              <User className="h-4 w-4 stroke-[1.9]" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 leading-tight">{emp.name}</div>
              {emp.cpf && <div className="text-[11px] font-medium text-slate-500 leading-tight mt-0.5">{emp.cpf}</div>}
            </div>
          </div>
        ) : null;
      })}
    </div>
  );

  const renderInvolvedEmployeesSummary = (ids: number[] = []) => {
    const names = ids
      .map(id => employees.find(e => e.id === id)?.name)
      .filter((name): name is string => Boolean(name));

    if (names.length === 0) return { text: '-', count: 0 };

    const preview = names.slice(0, 2).join(', ');
    return {
      text: names.length > 2 ? `${preview} +${names.length - 2}` : preview,
      count: names.length,
    };
  };

  const filteredIncidents = useMemo(() => incidents.filter(i => {
    const term = searchTerm.trim().toLowerCase();
    const vehicle = vehicles.find((item) => item.id === i.vehicle_id);
    const involvedEmployeesText = (i.involved_employees || [])
      .map((employeeId) => {
        const employee = employees.find((item) => item.id === employeeId);
        return `${employee?.name || ''} ${employee?.cpf || ''}`.trim();
      })
      .join(' ')
      .toLowerCase();

    const matchesSearch =
      !term ||
      String(i.id).includes(term) ||
      (i.description || '').toLowerCase().includes(term) ||
      (i.date || '').toLowerCase().includes(term) ||
      (i.time || '').toLowerCase().includes(term) ||
      (i.location || '').toLowerCase().includes(term) ||
      `${vehicle?.brand || ''} ${vehicle?.license_plate || ''}`.toLowerCase().includes(term) ||
      involvedEmployeesText.includes(term);

    const selectedVehicles = appliedFilters.Veículo || [];
    const matchesVehicle =
      selectedVehicles.length === 0 || selectedVehicles.includes(String(i.vehicle_id));

    const selectedPhotoFilters = appliedFilters.Fotos || [];
    const hasPhotos = Boolean(i.images?.length);
    const matchesPhotos =
      selectedPhotoFilters.length === 0 ||
      (selectedPhotoFilters.includes('com_fotos') && hasPhotos) ||
      (selectedPhotoFilters.includes('sem_fotos') && !hasPhotos);

    return matchesSearch && matchesVehicle && matchesPhotos;
  }), [incidents, searchTerm, appliedFilters, vehicles, employees]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(filteredIncidents.length / ITEMS_PER_PAGE)),
    [filteredIncidents.length],
  );

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [currentPage, totalPaginas]);

  const { incidentsPaginados, pageRange } = useMemo(() => {
    const total = filteredIncidents.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      incidentsPaginados: filteredIncidents.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredIncidents, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('pt-BR'); }
    catch { return '-'; }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleString('pt-BR'); }
    catch { return '-'; }
  };

  const handleViewIncident = (i: Incident) => {
    setSelectedIncident(i);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setSelectedIncident(null);
    setIsViewModalOpen(false);
  };

  const handleImageClick = (url: string) => setSelectedImage(url);
  const handleCloseImageModal = () => setSelectedImage(null);

  return (
    <>
      <PageHeader
        title="Histórico de Incidentes"
        description="Incidentes registrados · Histórico completo de incidentes — acompanhe todos os incidentes com detalhes, fotos e envolvidos."
        hideTitleInBody
      />

      {toast && (
        <div className={`fixed top-5 right-5 z-[100] inline-flex items-center gap-2.5 rounded-xl px-4 py-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)] border animate-[fadeIn_0.2s_ease-out] ${toast.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
          <span className="text-[13px] font-semibold leading-tight">{toast.message}</span>
        </div>
      )}

      <div className="flex flex-col gap-5">
        {_error && (
          <div className="inline-flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div className="flex flex-col gap-0.5">
              <span className="text-[13px] font-bold leading-tight">Não foi possível carregar</span>
              <span className="text-[12.5px] font-medium leading-tight text-rose-700/90">{_error}</span>
            </div>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">Todos os incidentes</h2>
                <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                  Registros de ocorrências, colisões e problemas
                </p>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1 sm:max-w-xl">
                <div className="relative flex-1 min-w-0">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    placeholder="Buscar ID, local, veículo, descrição..."
                    className="block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-10 text-sm text-slate-900 placeholder:text-slate-400 shadow-[0_1px_2px_0_rgba(15,23,42,0.02)] transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 h-10"
                  />
                </div>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => setIsFilterOpen(true)}
                  className="h-10 shrink-0"
                >
                  <Filter className="h-4 w-4 stroke-[1.9]" />
                  Filtros
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => { setSubmitting(true); void fetchData().finally(() => setSubmitting(false)); }}
                  title="Atualizar informações"
                  className="h-10 shrink-0"
                  disabled={submitting}
                >
                  <RefreshCw className={`h-4 w-4 stroke-[1.9] ${submitting ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && incidents.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">Carregando histórico...</span>
              </div>
            ) : (
              <ModernTable className="table-fixed layout-fixed">
                <Thead>
                  <Tr>
                    <Th className="w-[6%] min-w-[80px] max-w-[100px]">ID</Th>
                    <Th className="w-[15%] min-w-[200px] max-w-[280px]">Veículo</Th>
                    <Th className="w-[12%] min-w-[180px] max-w-[240px]">Local</Th>
                    <Th className="w-[8%] min-w-[130px] max-w-[160px]">Data</Th>
                    <Th className="w-[7%] min-w-[100px] max-w-[130px]">Hora</Th>
                    <Th className="w-[18%] min-w-[220px] max-w-[320px]">Descrição</Th>
                    <Th className="w-[13%] min-w-[180px] max-w-[260px]">Envolvidos</Th>
                    <Th className="w-[10%] min-w-[120px] max-w-[160px]">Fotos</Th>
                    <Th className="w-[11%] min-w-[150px] max-w-[190px]">Criado em</Th>
                    <Th className="w-[6%] min-w-[80px] max-w-[100px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {incidentsPaginados.length === 0 ? (
                    <Tr>
                      <Td colSpan={10}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <AlertTriangle className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">Nenhum incidente encontrado</div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm ? 'Nenhum resultado para a busca atual. Tente outro termo.' : 'Não há incidentes registrados no sistema.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    incidentsPaginados.map((i) => {
                      const vehicleLabel = getVehicleInfo(i.vehicle_id);
                      const involvedSummary = renderInvolvedEmployeesSummary(i.involved_employees);
                      const photoCount = i.images?.length || 0;
                      return (
                        <Tr key={i.id}>
                          <Td>
                            <div className="inline-flex items-center gap-1.5 rounded-lg bg-rose-50 px-2.5 py-1 ring-1 ring-inset ring-rose-200">
                              <span className="text-[12.5px] font-bold leading-none tabular-nums text-rose-700">#{i.id}</span>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[280px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                                  <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={vehicleLabel.display}>
                                    {vehicleLabel.primary}
                                  </span>
                                  {vehicleLabel.secondary && (
                                    <span className="block truncate text-[11.5px] font-medium leading-tight text-slate-500 mt-0.5" title={vehicleLabel.secondary}>
                                      {vehicleLabel.secondary}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[240px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200">
                                  <MapPin className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13.5px] font-semibold leading-tight text-slate-900" title={i.location || '-'}>
                                    {i.location || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Calendar className="h-[15px] w-[15px] text-slate-400" />
                                {formatDate(i.date)}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                                <Clock className="h-[15px] w-[15px] text-slate-400" />
                                {i.time || '-'}
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[320px] overflow-hidden py-1">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                                  <FileText className="h-[18px] w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1 pt-0.5">
                                  <span className="block line-clamp-2 text-[13px] font-medium leading-snug text-slate-800" title={i.description}>
                                    {i.description}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="min-w-0 w-full max-w-[260px] overflow-hidden">
                              <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                  {involvedSummary.count > 1 ? (
                                    <Users className="h-[18px] w-[18px] stroke-[1.9]" />
                                  ) : (
                                    <User className="h-[18px] w-[18px] stroke-[1.9]" />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="block truncate text-[13px] font-medium leading-tight text-slate-800" title={involvedSummary.text}>
                                    {involvedSummary.text}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center gap-2">
                              {photoCount > 0 ? (
                                <>
                                  <div className="flex -space-x-2">
                                    {i.images!.slice(0, 3).map((img, idx) => (
                                      <button
                                        key={idx}
                                        onClick={() => handleImageClick(img.url)}
                                        className="group relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border-2 border-white bg-slate-100 shadow-sm ring-1 ring-slate-200 transition-all hover:ring-blue-300 hover:z-10"
                                        title="Ver foto"
                                      >
                                        <img
                                          src={img.url}
                                          alt={`Foto ${idx + 1}`}
                                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                                        />
                                      </button>
                                    ))}
                                  </div>
                                  <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold tabular-nums text-slate-700 ring-1 ring-inset ring-slate-200">
                                    {photoCount} foto(s)
                                  </span>
                                </>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 text-slate-400">
                                  <ImageIcon className="h-4 w-4 stroke-[1.8]" />
                                  <span className="text-[12px] font-semibold">Sem fotos</span>
                                </div>
                              )}
                            </div>
                          </Td>
                          <Td>
                            <div>
                              <div className="text-[12.5px] font-medium text-slate-700 tabular-nums leading-tight">{formatDate(i.created_at)}</div>
                              <div className="mt-0.5 text-[11px] font-semibold text-slate-500 tabular-nums leading-tight">{formatDateTime(i.created_at).split(' ')[1] || ''}</div>
                            </div>
                          </Td>
                          <Td>
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewIncident(i)}
                                title="Visualizar"
                                className="h-8 w-8 rounded-lg text-slate-500 hover:text-blue-700 hover:bg-blue-50 hover:ring-blue-200"
                              >
                                <Eye className="h-4 w-4 stroke-[1.9]" />
                              </Button>
                            </div>
                          </Td>
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.start}</span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.end}</span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">{pageRange.total}</span>
                  {' incidente(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          disabled={submitting}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1 ${n === currentPage ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]' : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'}`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/75 backdrop-blur-sm p-4 sm:p-6"
          onClick={handleCloseImageModal}
        >
          <div
            className="relative max-w-5xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleCloseImageModal}
              className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg ring-1 ring-slate-200 transition-all hover:bg-slate-100 hover:text-slate-900 z-10"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 stroke-[2]" />
            </button>
            <div className="overflow-hidden rounded-2xl border border-white/20 shadow-2xl bg-black/20">
              <img
                src={selectedImage}
                alt="Visualização completa"
                className="w-full max-h-[85vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}

      {isViewModalOpen && selectedIncident && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500 via-orange-500 to-orange-700 text-white shadow-[0_6px_16px_-8px_rgba(244,63,94,0.6)]">
                <AlertTriangle className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Incidente
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Informações completas do incidente #{selectedIncident.id}
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-6">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">Dados do incidente</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">ID</span>
                    <p className="text-sm text-slate-900 font-medium">#{selectedIncident.id}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Data</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDate(selectedIncident.date)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Hora</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{selectedIncident.time || '-'}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Local</span>
                  <p className="text-sm text-slate-900 font-medium">{selectedIncident.location || '-'}</p>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-semibold text-slate-500">Descrição</span>
                  <p className="text-sm text-slate-900 font-medium whitespace-pre-wrap leading-relaxed">{selectedIncident.description}</p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 to-teal-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-emerald-700">Veículo e Envolvidos</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <div>
                      {(() => {
                        const vLabel = getVehicleInfo(selectedIncident.vehicle_id);
                        return (
                          <div className="flex items-start gap-2.5">
                            <div className="mt-0.5 shrink-0 flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                              <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                            </div>
                            <div>
                              <p className="text-sm text-slate-900 font-semibold leading-tight">{vLabel.primary}</p>
                              {vLabel.secondary && <p className="text-[11.5px] font-semibold text-slate-500 mt-0.5 leading-tight">{vLabel.secondary}</p>}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-semibold text-slate-500">Criado em</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">{formatDateTime(selectedIncident.created_at)}</p>
                  </div>
                </div>
                <div className="flex flex-col gap-1 pt-4 border-t border-slate-200/80">
                  <span className="text-xs font-semibold text-slate-500">Funcionários envolvidos</span>
                  <div>
                    {selectedIncident.involved_employees && selectedIncident.involved_employees.length > 0
                      ? renderInvolvedEmployees(selectedIncident.involved_employees)
                      : <p className="text-sm font-medium text-slate-500">Nenhum funcionário associado.</p>}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">Fotos do incidente</h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {selectedIncident.images && selectedIncident.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {selectedIncident.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleImageClick(img.url)}
                        className="group relative aspect-square overflow-hidden rounded-xl border border-slate-200 bg-slate-50 transition-all hover:ring-2 hover:ring-blue-300 hover:shadow-lg"
                      >
                        <img
                          src={img.url}
                          alt={`Foto do incidente ${idx + 1}`}
                          className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-2">
                          <span className="text-[11px] font-bold text-white drop-shadow">Ver completa</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-slate-50 py-10 text-slate-400 border border-dashed border-slate-200">
                    <ImageIcon className="h-10 w-10 stroke-[1.8]" />
                    <span className="text-[12.5px] font-semibold">Nenhuma foto registrada para este incidente.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      <FilterOptions
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApplyFilter={(f) => { setAppliedFilters(f); setCurrentPage(1); }}
        filterCategories={filterCategories}
        initialFilters={appliedFilters}
      />
    </>
  );
};

export default HistoricoDeIncidentes;
